<script setup>
import { router } from '@inertiajs/vue3'
import TextareaInput from '@/Shared/TextareaInput.vue'
import LoadingButton from '@/Shared/LoadingButton.vue'
import { useForm } from '@inertiajs/vue3'
import { useConfirm } from '@/Composables/useConfirm'
import MultiselectInput from '@/Shared/MultiselectInput.vue'
import Portrait from '@/Shared/Portrait.vue'
import Icon from '@/Shared/Icon.vue'

const props = defineProps({
  student: {
    type: Object,
    required: true
  },
  levels: {
    type: Array,
    default: () => []
  }
})

let form = useForm({
  levels: props.student.levels,
  observations: props.student.observations
})

function update() {
  form.put(`/students/${props.student.id}/update`)
}

async function remove() {
  if (await useConfirm('¿Estas seguro de que quieres eliminar el alumno?')) {
    router.delete(`/students/${props.student.id}/delete`)
  }
}

const breadcrumbs = [
  { label: 'Clases' },
  { label: 'Alumnos', href: '/students' },
  { label: props.student.player.fullname }
]
</script>

<template>
  <Portrait
    :title="`Alumno: ${student.player.fullname}`"
    subtitle="Gestiona la información y niveles del alumno"
    :breadcrumbs="breadcrumbs"
    :footer-actions="true"
  >
    <div class="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden p-6">
      <div class="flex items-center mb-6 pb-4 border-b border-gray-100">
        <div
          class="h-16 w-16 rounded-full bg-indigo-100 flex items-center justify-center text-indigo-700 text-xl font-bold mr-4"
        >
          {{ student.player.name.charAt(0) }}{{ student.player.lastname.charAt(0) }}
        </div>
        <div>
          <h3 class="text-lg font-medium text-gray-900">{{ student.player.fullname }}</h3>
          <p class="text-sm text-gray-500 flex items-center">
            <Icon name="Phone" class="h-4 w-4 mr-1" />
            {{ student.player.phone }}
          </p>
        </div>
      </div>

      <form class="space-y-6" @submit.prevent="update">
        <div class="grid grid-cols-1 gap-6">
          <div class="bg-gray-50 p-4 rounded-md">
            <h4 class="text-sm font-medium text-gray-700 mb-2">Información</h4>
            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Nombre</label>
                <div class="bg-gray-100 text-gray-800 px-3 py-2 rounded-md">
                  {{ student.player.fullname }}
                </div>
              </div>
              <div>
                <label class="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <div class="bg-gray-100 text-gray-800 px-3 py-2 rounded-md">
                  {{ student.player.email }}
                </div>
              </div>
            </div>
          </div>

          <MultiselectInput
            v-model="form.levels"
            :options="levels"
            :error="form.errors.levels"
            label="Niveles"
            input-label="name"
            track-by="name"
          />

          <TextareaInput
            v-model="form.observations"
            :error="form.errors.observations"
            label="Observaciones"
            placeholder="Añade notas o comentarios sobre este alumno"
          />
        </div>
      </form>
    </div>

    <template #footer>
      <!-- Versión móvil -->
      <div class="w-full block sm:hidden">
        <div class="flex items-stretch justify-end w-full space-x-4">
          <button class="btn-red" type="button" @click="remove">Eliminar</button>

          <LoadingButton
            :loading="form.processing"
            class="btn-indigo"
            type="submit"
            @click="update"
          >
            Guardar
          </LoadingButton>
        </div>
      </div>

      <!-- Versión escritorio -->
      <div class="hidden sm:flex sm:items-center sm:justify-end w-full space-x-4">
        <button class="btn-red" type="button" @click="remove">Eliminar</button>

        <LoadingButton :loading="form.processing" class="btn-indigo" type="submit" @click="update">
          Guardar
        </LoadingButton>
      </div>
    </template>
  </Portrait>
</template>
